import { memo } from 'react';

import { TemplateMediaErrorIcon } from '@/components/icons';
import { useTranslate } from '@/hooks/useTranslate';

import { useTemplateCardHoverPreview } from '../../hooks/useTemplateCardHoverPreview';
import { TemplateCardSkeletonCell } from './TemplateCardSkeleton';
import type { TplTemplateVO } from './types';

interface TemplateCardProps {
  template: TplTemplateVO;
  /** 仅「复刻」按钮触发；卡片其它区域点击不再打开表单 */
  onReplicate?: (template: TplTemplateVO) => void;
}

const TemplateCard = memo<TemplateCardProps>(({ template, onReplicate }) => {
  const { t } = useTranslate();
  const {
    videoRef,
    coverRef,
    isCoverReady,
    shouldAttachVideo,
    showVideo,
    showLoadingOverlay,
    hasVideoError,
    onMouseEnter,
    onMouseLeave,
    onCoverLoad,
    onCoverError,
    onVideoPlaying,
    onVideoError,
  } = useTemplateCardHoverPreview({
    coverUrl: template.cover,
    previewUrl: template.preview,
  });

  return (
    <div
      className="group relative h-full cursor-default overflow-hidden rounded-[8px]"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="absolute inset-0 z-0 bg-[#F2F3F5]" />
      {!isCoverReady && (
        <div className="pointer-events-none absolute inset-0 z-[4]">
          <TemplateCardSkeletonCell fitContainer />
        </div>
      )}
      <img
        ref={coverRef}
        src={template.cover || ''}
        alt={template.name}
        onLoad={onCoverLoad}
        onError={onCoverError}
        className={`absolute inset-0 z-[1] h-full w-full object-cover object-bottom ${
          showVideo
            ? 'pointer-events-none opacity-0'
            : !isCoverReady
              ? 'opacity-0'
              : 'opacity-100 transition-opacity duration-150'
        }`}
      />
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        preload="none"
        className={`absolute inset-0 z-[2] h-full w-full object-cover bg-[#F6F9FF] ${
          showVideo ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        src={shouldAttachVideo ? template.preview : undefined}
        onLoadedData={() => {
          const v = videoRef.current;
          if (v && v.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            try {
              void v.play();
            } catch {
              /* play 由 effect 再试 */
            }
          }
        }}
        onPlaying={onVideoPlaying}
        onError={onVideoError}
      />

      <div
        className={`absolute inset-0 z-[10] flex items-center justify-center bg-black transition-opacity duration-300 ${
          showLoadingOverlay ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {!hasVideoError ? (
          <div className="h-[20px] w-[20px] animate-spin rounded-full border-2 border-[#4931FA]/40 border-t-[#4931FA]" />
        ) : (
          <TemplateMediaErrorIcon />
        )}
      </div>

      <div
        className={`absolute z-[11] h-[44px] w-full bottom-0 left-0 transition-all flex items-center justify-center ${
          showVideo ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          background: 'rgba(255, 255, 255, 0.35)',
          backdropFilter: 'blur(4px)',
        }}
      >
        <button
          type="button"
          className="h-[30px] w-[146px] rounded-[8px] bg-[#4931FA] text-center text-[14px] font-[500] leading-[30px] text-white opacity-0 transition-all duration-200 active:scale-95 translate-y-[8px] group-hover:translate-y-0 group-hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            onReplicate?.(template);
          }}
        >
          {t('home.tpl.remake')}
        </button>
      </div>
    </div>
  );
});

TemplateCard.displayName = 'TemplateCard';

export default TemplateCard;
